<template>
    <div class="tag-input-wrapper">
      <label class="field-label" :for="inputId">
        {{ label }}
        <span v-if="required" class="required">*</span>
      </label>
      <div 
        class="tags-container" 
        :class="{ 'focused': isFocused, 'has-tags': tags.length > 0 }"
        @click="focusInput"
      >
        <div class="tags-list" v-if="tags.length > 0">
          <span 
            v-for="(tag, index) in tags" 
            :key="`tag-${index}-${tag}`" 
            class="tag-item"
            :title="tag"
          >
            <span class="tag-text">{{ tag }}</span>
            <button 
              type="button"
              class="tag-remove" 
              @click.stop="removeTag(index)"
              :aria-label="`Remove ${tag} tag`"
            >
              ✕
            </button>
          </span>
        </div>
        <input
          :id="inputId"
          ref="tagInput"
          v-model="newTag"
          @keydown.enter.prevent="addTag"
          @keydown.backspace="handleBackspace"
          @keydown="handleKeydown"
          @focus="handleFocus"
          @blur="handleBlur"
          :placeholder="tags.length === 0 ? placeholder : ''"
          class="tag-input"
          autocomplete="off"
          spellcheck="false"
        />
      </div>
      <div class="field-help" v-if="showHelp">
        Press Enter or comma to add a tag. Backspace to remove the last tag.
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'TagInput',
    props: {
      modelValue: {
        type: Array,
        default: () => [],
      },
      label: {
        type: String,
        default: 'Tags',
      },
      placeholder: {
        type: String,
        default: 'Type and press Enter to add tags',
      },
      required: {
        type: Boolean,
        default: false,
      },
      maxTags: {
        type: Number,
        default: null,
      },
      allowDuplicates: {
        type: Boolean,
        default: false,
      },
      showHelp: {
        type: Boolean,
        default: true,
      },
    },
    emits: ['update:modelValue', 'tag-added', 'tag-removed'],
    data() {
      return {
        newTag: '',
        isFocused: false,
        inputId: `tag-input-${Math.random().toString(36).substr(2, 9)}`,
      };
    },
    computed: {
      tags: {
        get() {
          return this.modelValue || [];
        },
        set(value) {
          this.$emit('update:modelValue', value);
        },
      },
      canAddMore() {
        return !this.maxTags || this.tags.length < this.maxTags;
      },
    },
    methods: {
      addTag() {
        const trimmedTag = this.newTag.trim();
        
        if (!trimmedTag) return;
        if (!this.canAddMore) return;
        if (!this.allowDuplicates && this.tags.includes(trimmedTag)) return;
        
        const newTags = [...this.tags, trimmedTag];
        this.tags = newTags;
        this.$emit('tag-added', trimmedTag);
        this.newTag = '';
      },
      removeTag(index) {
        if (index < 0 || index >= this.tags.length) return;
        
        const removedTag = this.tags[index];
        const newTags = this.tags.filter((_, i) => i !== index);
        this.tags = newTags;
        this.$emit('tag-removed', removedTag);
      },
      handleBackspace() {
        if (this.newTag === '' && this.tags.length > 0) {
          this.removeTag(this.tags.length - 1);
        }
      },
      handleKeydown(event) {
        // Handle comma key (keyCode 188 or key ',')
        if (event.key === ',' || event.keyCode === 188) {
          event.preventDefault();
          this.addTag();
        }
      },
      handleFocus() {
        this.isFocused = true;
      },
      handleBlur() {
        this.isFocused = false;
        // Add tag on blur if there's content
        if (this.newTag.trim()) {
          this.addTag();
        }
      },
      focusInput() {
        this.$refs.tagInput?.focus();
      },
    },
  };
  </script>
  
  <style scoped>
  /* Modern Tag Input Styles */
  .tag-input-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .field-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 0;
    display: flex;
    align-items: center;
  }
  
  .required {
    color: #f56565;
    font-weight: 700;
    margin-left: 2px;
  }
  
  .tags-container {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    min-height: 42px;
    padding: 0.5rem;
    border: 2px solid #cbd5e0;
    border-radius: 0.5rem;
    background-color: #ffffff;
    cursor: text;
    transition: all 150ms ease-in-out;
    gap: 0.5rem;
  }
  
  .tags-container:hover {
    border-color: #a0aec0;
  }
  
  .tags-container.focused {
    border-color: #5b2e8e;
    box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
  }
  
  .tags-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
  }
  
  .tag-item {
    display: inline-flex;
    align-items: center;
    background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
    color: #ffffff;
    border-radius: 1rem;
    padding: 0.25rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 500;
    line-height: 1.2;
    max-width: 200px;
    box-shadow: 0 1px 2px rgba(91, 46, 142, 0.1);
    transition: all 150ms ease-in-out;
    position: relative;
  }
  
  .tag-item:hover {
    background: linear-gradient(135deg, #4a2373 0%, #5b2e8e 100%);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(91, 46, 142, 0.2);
  }
  
  .tag-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }
  
  .tag-remove {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: bold;
    margin-left: 0.5rem;
    padding: 0;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 150ms ease-in-out;
    flex-shrink: 0;
  }
  
  .tag-remove:hover {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
    transform: scale(1.1);
  }
  
  .tag-remove:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.3);
    color: #ffffff;
  }
  
  .tag-input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    padding: 0.25rem 0;
    font-size: 0.875rem;
    font-family: inherit;
    color: #2d3748;
    min-width: 120px;
    line-height: 1.5;
  }
  
  .tag-input::placeholder {
    color: #a0aec0;
    font-style: italic;
  }
  
  .field-help {
    font-size: 0.75rem;
    color: #718096;
    line-height: 1.4;
    margin-top: 0.25rem;
  }
  
  /* Empty state styling */
  .tags-container:not(.has-tags) .tag-input {
    width: 100%;
  }
  
  /* Responsive adjustments */
  @media (max-width: 640px) {
    .tag-item {
      max-width: 150px;
      font-size: 0.75rem;
      padding: 0.2rem 0.6rem;
    }
    
    .tag-input {
      min-width: 100px;
      font-size: 0.8125rem;
    }
    
    .tags-container {
      min-height: 38px;
      padding: 0.4rem;
    }
  }
  
  /* Focus visible for accessibility */
  .tag-remove:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 1px;
  }
  
  .tag-input:focus-visible {
    outline: none;
  }
  
  /* High contrast mode support */
  @media (prefers-contrast: high) {
    .tags-container {
      border-color: #4a5568;
    }
    
    .tag-item {
      background: #2d3748;
      border: 1px solid #ffffff;
    }
    
    .field-help {
      color: #4a5568;
    }
  }
  
  /* Reduced motion support */
  @media (prefers-reduced-motion: reduce) {
    .tag-item,
    .tag-remove,
    .tags-container {
      transition: none;
    }
  }
  </style>
